/**
 * @fileoverview React contexts for renderer-wide injectables.
 * @module inspection-renderer/contracts
 *
 * Responsibilities:
 * - Provide React `Context` carriers for the host-injected
 *   attachment, data-bound, search, photo, inspector, and offline services.
 * - Provide hooks that read active services and return `null` when an
 *   optional host integration is not configured.
 *
 * Dependencies:
 * - React 18 (peer).
 *
 * Architecture:
 * - Pure React Context. No state, no effects.
 *
 * @hook useAttachmentPicker
 * Purpose: read the host-injected attachment-picker (Camera/Photo/Signature).
 * Returns: AttachmentPicker | null
 * Side Effects: none.
 * Offline Behavior: host's picker is responsible for offline routing (D-0.20).
 *
 * @hook useResolveContext
 * Purpose: read the host-injected data-bound resolver.
 * Returns: ResolveContext | null
 * Side Effects: none.
 * Notes: Preserve existing behavior and synchronization invariants.
 */
import { createContext, useCallback, useContext, type ReactNode } from 'react';
import type { AttachmentPicker } from './attachmentPicker';
import type { ResolveContext } from './resolveContext';
import type { InspectorRosterEntry } from '../inspectors/inspectorSelection';
import type { ViolationCodeSetIndexEntry } from '../violations/violationSearch';
import type { AttachmentRefLike } from '@firesquirrel/core-domain';
import type { InspectionAuditEvent } from '@firesquirrel/core-domain';

/**
 * @contract ThumbnailResolver
 * Purpose: host-injected callback to resolve an attachment ID to a displayable
 * thumbnail URL. Mobile-pwa resolves from IndexedDB; tenant-web resolves via
 * SAS URL. Returns null if the thumbnail is not available.
 * Fields: (attachmentId: string) => Promise<string | null>
 * Rules: Keep the declared field types and optionality consistent at call sites.
 * Sync: Persistence and synchronization remain owned by the consuming service.
 */
export type ThumbnailResolver = (attachmentId: string) => Promise<string | null>;

/**
 * @contract ThumbnailUrlOwnership
 * Purpose: Declares who owns object URLs returned by the ThumbnailResolver.
 * Fields: 'owned' | 'shared' — see Rules.
 * Rules: 'owned' — each call returns a FRESH object URL the consumer may
 *          revoke when done (customer portal's per-call download resolver).
 *        'shared' — the resolver caches/pools URLs (tenant AttachmentUrlResolver);
 *          revoking one would poison the cache for every future mount, so
 *          consumers must never revoke. Defaults to 'shared' — resolvers
 *          are presumed cache-owning.
 * Sync: Read-only contract; no persistence — resolved URLs are session-scoped.
 */
export type ThumbnailUrlOwnership = 'owned' | 'shared';

/**
 * @contract CodeSetSearchProvider
 * Purpose: Supplies Dexie-backed code-set index entries to the shared violation search.
 * Fields: listIndexEntries(codeSetIds) returns entries scoped to the requested code sets.
 * Rules: Hosts provide records only; the renderer owns matching and ranking.
 * Sync: Read-only local data supplied by the host.
 */
export interface CodeSetSearchProvider {
  listIndexEntries(codeSetIds: string[]): Promise<ViolationCodeSetIndexEntry[]>;
}

/**
 * @contract ViolationPhotoCaptureResult
 * Purpose: Describes a captured violation photo without coupling the renderer to host storage.
 * Fields: Optional attachmentId, localId, and previewUrl identify persisted or displayable media.
 * Rules: At least one identifier or preview URL should be returned for a successful capture.
 * Sync: The host owns upload and offline persistence.
 */
export interface ViolationPhotoCaptureResult {
  attachmentId?: string;
  localId?: string;
  previewUrl?: string;
}

/**
 * @contract ViolationPhotoProvider
 * Purpose: Captures violation photos and resolves attachment thumbnails.
 * Fields: capture(file) stores the file through the host pipeline; resolveThumbnail is optional.
 * Rules: Missing provider hides capture controls; unavailable thumbnails return null.
 * Sync: The host owns local persistence and upload behavior.
 */
export interface ViolationPhotoProvider {
  capture(file: File): Promise<ViolationPhotoCaptureResult>;
  resolveThumbnail?(ref: AttachmentRefLike): Promise<string | null>;
}

/**
 * @contract InspectorIdentity
 * Purpose: Identifies the authenticated inspector for violation audit fields.
 * Fields: userId and userName are supplied by the host's existing identity source.
 * Rules: Identity is optional; the module remains usable when it is unavailable.
 * Sync: Identity is read-only session context.
 */
export interface InspectorIdentity {
  userId: string;
  userName: string;
}

/**
 * @contract InspectionAuditContext
 * Purpose: Supplies event identity and read-only state to discrete renderer actions.
 * Fields: Optional caseId/eventIndex, readOnly, and host audit-event callback.
 * Rules: Read-only contexts suppress audit delivery; callback failures never block actions.
 * Sync: The host callback writes through its local-first audit service.
 */
export interface InspectionAuditContext {
  caseId?: string;
  eventIndex?: number;
  readOnly?: boolean;
}

/**
 * @contract InspectionAuditEventHandler
 * Purpose: Receives one successful discrete inspection action from the renderer.
 * Fields: A typed InspectionAuditEvent and optional synchronous or asynchronous result.
 * Rules: The renderer catches callback errors and logs a warning.
 * Sync: The host owns audit persistence.
 */
export type InspectionAuditEventHandler = (event: InspectionAuditEvent) => void | Promise<void>;

/**
 * @contract InspectionTelemetryEventName
 * Purpose: Restricts renderer telemetry to the occurrence lifecycle events.
 * Fields: violation_occurrence_logged | violation_occurrence_repaired.
 * Rules: Hosts deliver events through their initialized web-telemetry singleton.
 * Sync: This telemetry contract is independent of local-first audit persistence.
 */
export type InspectionTelemetryEventName =
  | 'violation_occurrence_logged'
  | 'violation_occurrence_repaired';

/**
 * @contract InspectionTelemetryEventProperties
 * Purpose: Defines the sanitized dimensions allowed on occurrence telemetry.
 * Fields: formId, fieldId, codeSetId, code, occurrenceCount, eventIndex.
 * Rules: Do not add user identity, free-text occurrence data, or other payloads.
 * Sync: Telemetry does not participate in inspection persistence or sync.
 */
export interface InspectionTelemetryEventProperties {
  formId: string;
  fieldId: string;
  codeSetId: string;
  code: string;
  occurrenceCount: number;
  eventIndex: number;
}

/**
 * @contract InspectionTelemetryEventHandler
 * Purpose: Forwards a sanitized occurrence event to a host telemetry client.
 * Fields: Event name and the allowlisted occurrence dimensions.
 * Rules: Hosts supply the initialized web-telemetry singleton; failures do not block capture.
 * Sync: Telemetry is separate from local-first audit and persistence.
 */
export type InspectionTelemetryEventHandler = (
  name: InspectionTelemetryEventName,
  properties: InspectionTelemetryEventProperties,
) => void;

interface InspectionAuditProviderValue extends InspectionAuditContext {
  onInspectionAuditEvent?: InspectionAuditEventHandler;
}

const AttachmentPickerContext = createContext<AttachmentPicker | null>(null);
const ResolveContextCtx = createContext<ResolveContext | null>(null);
const OfflineContext = createContext<boolean>(false);
const InspectorRosterContext = createContext<readonly InspectorRosterEntry[]>([]);
const ThumbnailResolverContext = createContext<ThumbnailResolver | null>(null);
const ThumbnailUrlOwnershipContext = createContext<ThumbnailUrlOwnership>('shared');
const CodeSetSearchContext = createContext<CodeSetSearchProvider | null>(null);
const ViolationPhotoContext = createContext<ViolationPhotoProvider | null>(null);
const InspectionAuditContextValue = createContext<InspectionAuditProviderValue>({});
const InspectionTelemetryContext = createContext<InspectionTelemetryEventHandler | null>(null);

/**
 * @hook useAttachmentPicker
 * Purpose: Provides use attachment picker state to React consumers.
 * Returns: AttachmentPicker | null
 * Side Effects: Uses the subscriptions and service calls present in the implementation.
 * Offline Behavior: Preserves locally available state when the network is unavailable.
 */
export function useAttachmentPicker(): AttachmentPicker | null {
  return useContext(AttachmentPickerContext);
}

/**
 * @hook useResolveContext
 * Purpose: Provides use resolve context state to React consumers.
 * Returns: ResolveContext | null
 * Side Effects: Uses the subscriptions and service calls present in the implementation.
 * Offline Behavior: Preserves locally available state when the network is unavailable.
 */
export function useResolveContext(): ResolveContext | null {
  return useContext(ResolveContextCtx);
}

/** @hook useIsOffline — read host-injected offline flag (defaults to false).
 * Purpose: Provides use is offline state to React consumers.
 * Returns: boolean
 * Side Effects: Uses the subscriptions and service calls present in the implementation.
 * Offline Behavior: Preserves locally available state when the network is unavailable.
 */
export function useIsOffline(): boolean {
  return useContext(OfflineContext);
}

/** @hook useInspectorRoster — read host-injected event inspector roster.
 * Purpose: Provides use inspector roster state to React consumers.
 * Returns: readonly InspectorRosterEntry[]
 * Side Effects: Uses the subscriptions and service calls present in the implementation.
 * Offline Behavior: Preserves locally available state when the network is unavailable.
 */
export function useInspectorRoster(): readonly InspectorRosterEntry[] {
  return useContext(InspectorRosterContext);
}

/** @hook useThumbnailResolver — read host-injected thumbnail resolver (defaults to null).
 * Purpose: Provides use thumbnail resolver state to React consumers.
 * Returns: ThumbnailResolver | null
 * Side Effects: Uses the subscriptions and service calls present in the implementation.
 * Offline Behavior: Preserves locally available state when the network is unavailable.
 */
export function useThumbnailResolver(): ThumbnailResolver | null {
  return useContext(ThumbnailResolverContext);
}

/**
 * @hook useThumbnailUrlOwnership
 * Purpose: Reads whether the resolver's returned object URLs are
 *   consumer-owned (revokable) or resolver-cached (never revoked).
 * Returns: ThumbnailUrlOwnership — defaults to 'shared' (do not revoke).
 * Side Effects: none — pure context read.
 * Offline Behavior: context value is host-declared; identical offline.
 */
export function useThumbnailUrlOwnership(): ThumbnailUrlOwnership {
  return useContext(ThumbnailUrlOwnershipContext);
}

/** @hook useCodeSetSearch — read the host's optional Dexie-backed code-set index provider.
 * Purpose: Provides use code set search state to React consumers.
 * Returns: CodeSetSearchProvider | null
 * Side Effects: Uses the subscriptions and service calls present in the implementation.
 * Offline Behavior: Preserves locally available state when the network is unavailable.
 */
export function useCodeSetSearch(): CodeSetSearchProvider | null {
  return useContext(CodeSetSearchContext);
}

/** @hook useViolationPhotos — read the host's optional violation-photo provider.
 * Purpose: Provides use violation photos state to React consumers.
 * Returns: ViolationPhotoProvider | null
 * Side Effects: Uses the subscriptions and service calls present in the implementation.
 * Offline Behavior: Preserves locally available state when the network is unavailable.
 */
export function useViolationPhotos(): ViolationPhotoProvider | null {
  return useContext(ViolationPhotoContext);
}

/**
 * @hook useInspectionAuditEvent
 * Purpose: Delivers a discrete action to the host audit seam with case/event context.
 * Returns: A guarded emitter that silently skips read-only or unwired contexts.
 * Side Effects: Invokes the host callback and logs callback failures.
 * Offline Behavior: Persistence is delegated to the host's local-first audit service.
 * @errorHandling useInspectionAuditEvent
 * Strategy: Use the implementation’s existing catch, return, and propagation behavior.
 * User Impact: Callers can surface operation failures without silently treating them as success.
 * Logging: Retain existing operation logging and avoid sensitive values.
 * @logging useInspectionAuditEvent
 * Events: Record the operation outcome and actionable failures.
 * Level: Use the existing operation-appropriate info, warn, or error level.
 * Correlation: Correlate with the case, event, or operation identifier when available.
 */
export function useInspectionAuditEvent(): (
  event: InspectionAuditEvent,
  actionReadOnly?: boolean,
) => void {
  const context = useContext(InspectionAuditContextValue);
  return useCallback((event, actionReadOnly = false) => {
    if (actionReadOnly || context.readOnly || !context.onInspectionAuditEvent) return;
    const enrichedEvent: InspectionAuditEvent = {
      ...event,
      caseId: event.caseId ?? context.caseId,
      eventIndex: event.eventIndex ?? context.eventIndex,
    };
    try {
      const result = context.onInspectionAuditEvent(enrichedEvent);
      if (result && typeof result.then === 'function') {
        void result.catch((error: unknown) => {
          console.warn('[inspection-renderer] Inspection audit callback failed', error);
        });
      }
    } catch (error) {
      console.warn('[inspection-renderer] Inspection audit callback failed', error);
    }
  }, [context]);
}

/**
 * @hook useInspectionTelemetryEvent
 * Purpose: Delivers occurrence lifecycle events to the host-injected telemetry seam.
 * Returns: A guarded event emitter that does not expose free-text occurrence data.
 * Side Effects: Calls the host telemetry callback and catches callback failures.
 * Offline Behavior: The host web-telemetry singleton owns offline buffering.
 * @logging useInspectionTelemetryEvent
 * Events: violation_occurrence_logged, violation_occurrence_repaired.
 * Level: info.
 * Correlation: formId and eventIndex are included in every event.
 */
export function useInspectionTelemetryEvent(): InspectionTelemetryEventHandler {
  const onTelemetryEvent = useContext(InspectionTelemetryContext);
  return useCallback((name, properties) => {
    if (!onTelemetryEvent) return;
    try {
      onTelemetryEvent(name, properties);
    } catch (error) {
      console.warn('[inspection-renderer] Inspection telemetry callback failed', error);
    }
  }, [onTelemetryEvent]);
}

/**
 * @contract RendererProvidersProps
 * Purpose: prop contract for the single context-injection wrap.
 * Fields: attachmentPicker, resolveContext, isOffline, inspectorRoster, thumbnailResolver, codeSetSearch, violationPhotos, onInspectionAuditEvent, onTelemetryEvent, inspectionAuditContext, children
 * Rules: Keep the declared field types and optionality consistent at call sites.
 * Sync: Persistence and synchronization remain owned by the consuming service.
 */
export interface RendererProvidersProps {
  attachmentPicker?: AttachmentPicker;
  resolveContext?: ResolveContext;
  /** Whether the host is currently offline (drives offline pill on widgets). */
  isOffline?: boolean;
  /** Event inspector roster used by the `inspectors` widget. */
  inspectorRoster?: readonly InspectorRosterEntry[];
  /** Host-injected thumbnail resolver for visual photo previews. */
  thumbnailResolver?: ThumbnailResolver;
  /** Ownership of URLs the resolver returns — 'owned' means each call is a
   *  fresh object URL the renderer may revoke; 'shared' (default) means the
   *  resolver caches URLs and consumers must never revoke them. */
  thumbnailUrlOwnership?: ThumbnailUrlOwnership;
  /** Dexie-backed violation index source; ranking remains renderer-owned. */
  codeSetSearch?: CodeSetSearchProvider;
  /** Host-owned violation capture and thumbnail services. */
  violationPhotos?: ViolationPhotoProvider;
  /** Host callback for explicit inspection actions; never called by autosave. */
  onInspectionAuditEvent?: InspectionAuditEventHandler;
  /** Host callback for sanitized renderer occurrence telemetry. */
  onTelemetryEvent?: InspectionTelemetryEventHandler;
  /** Case/event identity and read-only state for renderer-originated audit events. */
  inspectionAuditContext?: InspectionAuditContext;
  children: ReactNode;
}

/**
 * @component RendererProviders
 * Purpose: single wrap that injects all renderer-wide contexts. Hosts mount
 * this once at the top of the `<InspectionRenderer>` tree.
 *
 * Props:
 * - attachmentPicker: optional. When omitted, Camera/Photo/Signature
 *   widgets render their capture buttons as disabled (no host wired).
 * - resolveContext: optional. When omitted, DataBound widget falls back
 *   to its `extras.placeholder`.
 *
 * Behavior: pure context provider.
 * Data Source: n/a.
 * Constraints: no API calls; no Dexie writes.
 * Performance: passes references through; consumers re-render only when
 *   the context value identity changes.
 */
export function RendererProviders(props: RendererProvidersProps): ReactNode {
  const {
    attachmentPicker,
    resolveContext,
    isOffline,
    inspectorRoster,
    thumbnailResolver,
    thumbnailUrlOwnership,
    codeSetSearch,
    violationPhotos,
    onInspectionAuditEvent,
    onTelemetryEvent,
    inspectionAuditContext,
    children,
  } = props;
  return (
    <AttachmentPickerContext.Provider value={attachmentPicker ?? null}>
      <ResolveContextCtx.Provider value={resolveContext ?? null}>
        <OfflineContext.Provider value={!!isOffline}>
          <InspectorRosterContext.Provider value={inspectorRoster ?? []}>
            <ThumbnailResolverContext.Provider value={thumbnailResolver ?? null}>
              <ThumbnailUrlOwnershipContext.Provider value={thumbnailUrlOwnership ?? 'shared'}>
              <CodeSetSearchContext.Provider value={codeSetSearch ?? null}>
            <ViolationPhotoContext.Provider value={violationPhotos ?? null}>
              <InspectionAuditContextValue.Provider value={{
                ...inspectionAuditContext,
                onInspectionAuditEvent,
              }}>
                <InspectionTelemetryContext.Provider value={onTelemetryEvent ?? null}>
                  {children}
                </InspectionTelemetryContext.Provider>
              </InspectionAuditContextValue.Provider>
            </ViolationPhotoContext.Provider>
              </CodeSetSearchContext.Provider>
              </ThumbnailUrlOwnershipContext.Provider>
            </ThumbnailResolverContext.Provider>
          </InspectorRosterContext.Provider>
        </OfflineContext.Provider>
      </ResolveContextCtx.Provider>
    </AttachmentPickerContext.Provider>
  );
}
